import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight, Mail, Handshake, ChevronDown } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLLAB_OPTIONS = [
  "Consultation Support",
  "Volunteering",
  "Partnerships",
  "General Inquiry",
];

const initialForm = { full_name: "", organisation: "", phone: "", email: "", message: "" };

export default function ContactUs() {
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const setField = (k) => (e) => {
    let v = e.target.value;
    if (k === "phone") v = v.replace(/\D/g, "").slice(0, 10);
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((er) => ({ ...er, [k]: undefined }));
  };

  const validate = () => {
    const er = {};
    if (!form.full_name.trim()) er.full_name = "Full name is required";
    if (!form.organisation.trim()) er.organisation = "Please select an option";
    if (!/^\d{10}$/.test(form.phone)) er.phone = "Phone must be exactly 10 digits";
    if (!form.email.trim()) er.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) er.email = "Enter a valid email";
    setErrors(er);
    return Object.keys(er).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thank you — we'll be in touch soon.");
      setForm(initialForm);
    } catch (err) {
      const msg = err?.response?.data?.detail?.[0]?.msg || err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="page-contact" className="bg-[#FAFAFA]">
      <section className="pt-40 pb-16">
        <div className="container-saksham">
          <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="text-xs md:text-sm uppercase tracking-[0.3em] text-[#09B792] font-semibold mb-6">
            Contact Us
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.1 }} className="font-serif text-6xl md:text-7xl lg:text-8xl leading-[0.98] tracking-tight font-medium max-w-4xl">
            Let&apos;s start a <em className="text-[#09B792] not-italic">conversation.</em>
          </motion.h1>
        </div>
      </section>

      <section className="pb-28">
        <div className="container-saksham grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-5">
            <p className="text-base md:text-lg text-[#0A0A0A]/80 leading-relaxed font-light">
              Whether you&apos;re a micropreneur looking for guidance, a partner organisation, or a donor — we&apos;d love to hear from you. Drop us a note and a real human will get back to you.
            </p>

            <div className="mt-12 space-y-6">
              <div className="flex items-start gap-4">
                <span className="w-10 h-10 bg-[#FBD223] text-[#0A0A0A] flex items-center justify-center">
                  <Mail size={16} />
                </span>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[#4A4A4A] font-semibold">Email</div>
                  <div className="text-sm mt-1" data-testid="contact-email">info@sakshamconsulting.in</div>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <form
              onSubmit={submit}
              data-testid="contact-form"
              className="bg-white border border-black/5 p-8 md:p-12"
              noValidate
            >
              <div className="space-y-7">
                <Field label="Full Name" required error={errors.full_name}>
                  <input
                    type="text"
                    data-testid="contact-input-name"
                    value={form.full_name}
                    onChange={setField("full_name")}
                    placeholder="Your full name"
                    className="w-full bg-transparent border-b border-black/20 focus:border-[#09B792] outline-none py-3 text-base"
                  />
                </Field>

                <Field label="How can we collaborate?" required error={errors.organisation} icon={<Handshake size={14} />}>
                  <div className="relative">
                    <select
                      data-testid="contact-input-organisation"
                      value={form.organisation}
                      onChange={setField("organisation")}
                      className={`w-full appearance-none bg-transparent border-b border-black/20 focus:border-[#09B792] outline-none py-3 pr-8 text-base cursor-pointer ${form.organisation ? "text-[#0A0A0A]" : "text-[#4A4A4A]/60"}`}
                    >
                      <option value="" disabled>Select an option…</option>
                      {COLLAB_OPTIONS.map((opt) => (
                        <option key={opt} value={opt} className="text-[#0A0A0A]">{opt}</option>
                      ))}
                    </select>
                    <ChevronDown size={16} className="pointer-events-none absolute right-1 top-1/2 -translate-y-1/2 text-[#4A4A4A]" />
                  </div>
                </Field>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-7">
                  <Field label="Phone Number" required error={errors.phone}>
                    <div className="flex items-center gap-2 border-b border-black/20 focus-within:border-[#09B792]">
                      <span className="text-sm text-[#4A4A4A] py-3">+91</span>
                      <input
                        type="tel"
                        inputMode="numeric"
                        maxLength={10}
                        data-testid="contact-input-phone"
                        value={form.phone}
                        onChange={setField("phone")}
                        placeholder="10-digit number"
                        className="w-full bg-transparent outline-none py-3 text-base"
                      />
                    </div>
                  </Field>
                  <Field label="Email" required error={errors.email}>
                    <input
                      type="email"
                      data-testid="contact-input-email"
                      value={form.email}
                      onChange={setField("email")}
                      placeholder="you@example.com"
                      className="w-full bg-transparent border-b border-black/20 focus:border-[#09B792] outline-none py-3 text-base"
                    />
                  </Field>
                </div>

                <Field label="Tell us a little more (optional)">
                  <textarea
                    data-testid="contact-input-message"
                    value={form.message}
                    onChange={setField("message")}
                    placeholder="Any context you'd like to share…"
                    rows={4}
                    className="w-full bg-transparent border-b border-black/20 focus:border-[#09B792] outline-none py-3 text-base resize-none"
                  />
                </Field>

                <div className="pt-2">
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="contact-submit-button"
                    className="inline-flex items-center gap-2 bg-[#0A0A0A] text-[#FBD223] hover:bg-[#FBD223] hover:text-[#0A0A0A] disabled:opacity-50 disabled:cursor-not-allowed px-8 py-4 text-sm font-medium transition-colors"
                  >
                    {submitting ? "Submitting…" : "Submit"} <ArrowRight size={16} />
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ label, required, error, children, icon }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.22em] text-[#4A4A4A] font-semibold flex items-center gap-2">
        {icon}
        {label} {required && <span className="text-[#09B792]">*</span>}
      </label>
      <div className="mt-1">{children}</div>
      {error && <div className="mt-2 text-xs text-red-600" data-testid="contact-error">{error}</div>}
    </div>
  );
}
